import { useEffect, useState } from "react";
import { useTeacherLiveClasses, type TeacherLiveClass } from "@/hooks/useTeacherLiveClasses";
import { useStudents } from "@/hooks/useStudents";
import { useAuth } from "@/hooks/useAuth";
import LiveClassRoom from "@/pages/ogretmen/components/LiveClassRoom";
import StudentPicker from "./components/StudentPicker";
import AttendanceModal from "@/pages/ogretmen/components/AttendanceModal";
import QrYoklamaModal from "@/pages/ogretmen/components/QrYoklamaModal";
import Modal from "@/components/base/Modal";
import { useClassAttendance } from "@/hooks/useAttendance";
import { gradeOptions, subjectOptions } from "@/data/panels";

const apiBase = ((import.meta.env.VITE_API_URL as string | undefined) || (import.meta.env.PROD ? "/api" : "http://localhost:8080/api")).replace(/\/$/, "");
function openDesktopMeeting(ticket: string) {
  const desktopUrl = `dersdinle-toplanti://join?ticket=${encodeURIComponent(ticket)}`;
  window.location.assign(desktopUrl);
}

const statusLabels: Record<string, string> = {
  planned: "Planlandı",
  live: "Canlı",
  ended: "Tamamlandı",
  cancelled: "İptal",
};

const statusColors: Record<string, string> = {
  planned: "bg-secondary-100 text-secondary-800",
  live: "bg-red-50 text-red-700 animate-pulse",
  ended: "bg-background-100 text-foreground-600",
  cancelled: "bg-background-100 text-foreground-400 line-through",
};

const inputClass =
  "w-full h-10 px-3 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 bg-background-50";

export default function CanliDersler() {
  const { data, loading, error, reload, createLiveClass, endLive, cancelClass, updateStudents } =
    useTeacherLiveClasses();
  const students = useStudents();
  const { user } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [launchMessage, setLaunchMessage] = useState<string | null>(null);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [activeClass, setActiveClass] = useState<TeacherLiveClass | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [editingClass, setEditingClass] = useState<TeacherLiveClass | null>(null);
  const [editStudentIds, setEditStudentIds] = useState<string[]>([]);
  const [savingStudents, setSavingStudents] = useState(false);
  const [attendanceClass, setAttendanceClass] = useState<TeacherLiveClass | null>(null);
  const [qrClass, setQrClass] = useState<TeacherLiveClass | null>(null);

  useEffect(() => {
    if (!launchMessage) return;
    const timer = window.setInterval(reload, 5000);
    return () => window.clearInterval(timer);
  }, [launchMessage, reload]);

  const studentsById = new Map((students.data ?? []).map((s) => [s.userId, s.name]));

  const studentName = (userId: string) => studentsById.get(userId) ?? "Öğrenci";

  const attendanceData = useClassAttendance(attendanceClass?.id ?? null);
  const attendanceStudents = (attendanceClass?.studentIds ?? []).map((uid) => ({
    userId: uid,
    name: studentName(uid),
  }));

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    const form = e.currentTarget;
    const fd = new FormData(form);
    const title = String(fd.get("title") || "").trim();
    const subject = String(fd.get("subject") || "").trim();
    const grade = String(fd.get("grade") || "").trim();
    const date = String(fd.get("date") || "").trim();
    const time = String(fd.get("time") || "").trim();
    const duration = Number(fd.get("duration") || 0);
    const description = String(fd.get("description") || "").trim();

    if (!title || !subject || !grade || !date || !time) {
      setFormError("Lütfen zorunlu alanları doldurun.");
      return;
    }
    if (!selectedStudentIds.length) {
      setFormError("Otomatik katılım için derse en az bir öğrenci atayın.");
      return;
    }

    const starts_at = new Date(`${date}T${time}:00`).toISOString();

    setBusy(true);
    try {
      await createLiveClass({ title, subject, grade, starts_at, duration_minutes: duration, description, studentIds: selectedStudentIds });
      setShowCreate(false);
      setSelectedStudentIds([]);
      form.reset();
    } catch {
      setFormError("Canlı ders oluşturulurken bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  const handleStart = async (id: string) => {
    setBusy(true);
    setLaunchError(null);
    setLaunchMessage(null);
    try {
      const response = await fetch(`${apiBase}/meeting/bridge-ticket`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ class_id: id }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ticket) throw new Error(payload.error || "Canlı ders bağlantısı hazırlanamadı.");
      openDesktopMeeting(payload.ticket);
      setLaunchMessage("Toplantı açılıyor ve sınıfınız hazırlanıyor. Atadığınız öğrenciler, odayı açtığınızda açık olan Meraklı üzerinden otomatik katılacak. Tarayıcı izin sorarsa Toplantı’yı açmayı seçin.");
      reload();
    } catch (error) {
      setLaunchError(error instanceof Error ? error.message : "Toplantı açılamadı. Yeniden deneyin.");
    } finally {
      setBusy(false);
    }
  };

  const handleEnd = async (id: string) => {
    setBusy(true);
    try {
      await endLive(id);
      setActiveClass(null);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async (id: string) => {
    setBusy(true);
    try {
      await cancelClass(id);
    } finally {
      setBusy(false);
    }
  };

  const openEditStudents = (lc: TeacherLiveClass) => {
    setEditingClass(lc);
    setEditStudentIds(lc.studentIds);
  };

  const saveStudents = async () => {
    if (!editingClass) return;
    setSavingStudents(true);
    try {
      await updateStudents(editingClass.id, editStudentIds);
      setEditingClass(null);
    } finally {
      setSavingStudents(false);
    }
  };

  if (activeClass) {
    return (
      <LiveClassRoom
        title={activeClass.title}
        roomCode={activeClass.roomCode}
        hostName={activeClass.teacher}
        hostIdentity={user?.id ?? ""}
        onEnd={() => handleEnd(activeClass.id)}
        onExit={() => setActiveClass(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Canlı Dersler</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Canlı derslerini planla, katılacak öğrencileri seç ve başlat.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate((v) => !v)}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line text-base" aria-hidden="true"></i>
          Yeni Canlı Ders
        </button>
      </div>

      {/* Yeni Ders Formu */}
      {launchMessage && <div role="status" className="rounded-xl bg-primary-50 p-4 text-primary-900">{launchMessage} <a href="/downloads/Toplanti-Kurulum.exe" className="underline font-semibold">Toplantı kurulumunu indir</a></div>}
      {launchError && <div role="alert" className="rounded-xl bg-red-50 p-4 text-red-700">{launchError}</div>}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-background-50 rounded-xl border border-background-200 p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground-950">Yeni Canlı Ders Oluştur</h2>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors"
            >
              <i className="ri-close-line" aria-hidden="true"></i>
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ders Başlığı</label>
              <input
                type="text"
                name="title"
                required
                placeholder="Örn: TYT Matematik: Fonksiyonlar"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ders</label>
              <select name="subject" required className={inputClass}>
                <option value="">Seçin...</option>
                {subjectOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Sınıf / Seviye</label>
              <select name="grade" required className={inputClass}>
                <option value="">Seçin...</option>
                {gradeOptions.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Süre (dk)</label>
              <input type="number" name="duration" min={15} max={180} defaultValue={90} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tarih</label>
              <input type="date" name="date" required className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Saat</label>
              <input type="time" name="time" required className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Açıklama</label>
              <textarea
                name="description"
                rows={1}
                placeholder="İşlenecek konular..."
                className={`${inputClass} h-10 resize-none`}
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Katılacak Öğrenciler
                <span className="ml-1 text-foreground-400 font-normal">(boş bırakılırsa tüm öğrenciler katılabilir)</span>
              </label>
              <StudentPicker
                students={students.data ?? []}
                selectedIds={selectedStudentIds}
                onChange={setSelectedStudentIds}
              />
            </div>
          </div>

          {formError && (
            <p className="mt-4 text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{formError}</p>
          )}

          <div className="flex items-center justify-end gap-3 mt-5">
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={busy}
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {busy ? "Oluşturuluyor..." : "Dersi Planla"}
            </button>
          </div>
        </form>
      )}

      {/* Durum */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center justify-between gap-3">
          <p className="text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="h-9 px-3 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
          >
            Tekrar dene
          </button>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16">
          <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-loader-4-line text-xl animate-spin" aria-hidden="true"></i>
          </span>
        </div>
      )}

      {!loading && !error && (data?.length ?? 0) === 0 && (
        <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-12 flex flex-col items-center justify-center text-center">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-vidicon-line text-2xl" aria-hidden="true"></i>
          </span>
          <p className="mt-4 font-heading font-semibold text-base text-foreground-700">Henüz canlı ders yok</p>
          <p className="mt-1.5 text-sm text-foreground-500 max-w-md">
            İlk canlı dersini oluşturmak için yukarıdaki "Yeni Canlı Ders" butonuna tıkla.
          </p>
        </div>
      )}

      {/* Ders listesi */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {(data ?? []).map((lc) => {
          const selectedCount = lc.studentIds.length;
          const shownNames = lc.studentIds.slice(0, 3).map(studentName);
          return (
            <div
              key={lc.id}
              className="bg-background-50 rounded-xl border border-background-200 p-5 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${statusColors[lc.status]}`}>
                  {statusLabels[lc.status]}
                  {lc.status === "live" && (
                    <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" aria-hidden="true"></span>
                  )}
                </span>
                <span className="text-xs text-foreground-400">{lc.duration} dk</span>
              </div>

              <h3 className="text-base font-semibold text-foreground-950 mb-1">{lc.title}</h3>
              <p className="text-sm text-foreground-500 mb-3">{lc.subject}{lc.grade ? ` · ${lc.grade}` : ""}</p>

              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-4">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  {lc.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {lc.time}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-key-2-line" aria-hidden="true"></i>
                  {lc.roomCode}
                </span>
              </div>

              {lc.description && (
                <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">{lc.description}</p>
              )}

              {/* Katılacak öğrenciler */}
              <div className="mb-4">
                <div className="flex items-center gap-1.5 text-xs text-foreground-500 mb-2">
                  <i className="ri-group-line" aria-hidden="true"></i>
                  {selectedCount === 0
                    ? "Tüm öğrenciler katılabilir"
                    : `${selectedCount} öğrenci`}
                </div>
                {selectedCount > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {shownNames.map((name) => (
                      <span
                        key={name}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium"
                      >
                        {name}
                      </span>
                    ))}
                    {selectedCount > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-background-100 text-foreground-500 text-xs font-medium">
                        +{selectedCount - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                {lc.status === "planned" && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleStart(lc.id)}
                      disabled={busy}
                      className="flex-1 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
                    >
                      <i className="ri-live-line" aria-hidden="true"></i>
                      Dersi Başlat
                    </button>
                    <button
                      type="button"
                      onClick={() => setAttendanceClass(lc)}
                      className="w-10 h-10 rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors inline-flex items-center justify-center"
                      aria-label="Yoklama al"
                      title="Yoklama Al"
                    >
                      <i className="ri-file-list-3-line" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => setQrClass(lc)}
                      className="w-10 h-10 rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors inline-flex items-center justify-center"
                      aria-label="QR yoklama"
                      title="QR ile Yoklama"
                    >
                      <i className="ri-qr-code-line" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => openEditStudents(lc)}
                      className="w-10 h-10 rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors inline-flex items-center justify-center"
                      aria-label="Katılacak öğrencileri düzenle"
                      title="Öğrencileri düzenle"
                    >
                      <i className="ri-group-line" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCancel(lc.id)}
                      disabled={busy}
                      className="w-10 h-10 rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 disabled:opacity-60 transition-colors inline-flex items-center justify-center"
                      aria-label="Dersi iptal et"
                    >
                      <i className="ri-close-line" aria-hidden="true"></i>
                    </button>
                  </>
                )}
                {lc.status === "live" && (
                  <>
                    <button
                      type="button"
                      onClick={() => setActiveClass(lc)}
                      disabled={busy}
                      className="flex-1 h-10 rounded-md bg-red-50 text-red-700 text-sm font-semibold hover:bg-red-100 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
                    >
                      <i className="ri-live-line" aria-hidden="true"></i>
                      Derse Dön
                    </button>
                    <button
                      type="button"
                      onClick={() => setAttendanceClass(lc)}
                      className="w-10 h-10 rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors inline-flex items-center justify-center"
                      aria-label="Yoklama al"
                      title="Yoklama Al"
                    >
                      <i className="ri-file-list-3-line" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => setQrClass(lc)}
                      className="w-10 h-10 rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors inline-flex items-center justify-center"
                      aria-label="QR yoklama"
                      title="QR ile Yoklama"
                    >
                      <i className="ri-qr-code-line" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => openEditStudents(lc)}
                      className="w-10 h-10 rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors inline-flex items-center justify-center"
                      aria-label="Katılacak öğrencileri düzenle"
                      title="Öğrencileri düzenle"
                    >
                      <i className="ri-group-line" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEnd(lc.id)}
                      disabled={busy}
                      className="h-10 px-3 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 transition-colors whitespace-nowrap"
                    >
                      Bitir
                    </button>
                  </>
                )}
                {lc.status === "ended" && (
                  <>
                    <span className="flex-1 h-10 rounded-md bg-background-100 text-foreground-400 text-sm font-medium inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <i className="ri-check-line" aria-hidden="true"></i>
                      Tamamlandı
                    </span>
                    <button
                      type="button"
                      onClick={() => setAttendanceClass(lc)}
                      className="w-10 h-10 rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors inline-flex items-center justify-center"
                      aria-label="Yoklamayı görüntüle"
                      title="Yoklama"
                    >
                      <i className="ri-file-list-3-line" aria-hidden="true"></i>
                    </button>
                  </>
                )}
                {lc.status === "cancelled" && (
                  <span className="flex-1 h-10 rounded-md bg-background-100 text-foreground-400 text-sm font-medium inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
                    <i className="ri-close-circle-line" aria-hidden="true"></i>
                    İptal edildi
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Öğrenci düzenleme modalı */}
      <Modal
        open={!!editingClass}
        onClose={() => setEditingClass(null)}
        title="Katılacak Öğrenciler"
        titleId="edit-students-title"
        id="edit-students-modal"
        maxWidthClass="max-w-lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground-600">
            <span className="font-semibold text-foreground-900">{editingClass?.title}</span> dersine katılacak öğrencileri seç.
          </p>
          <StudentPicker
            students={students.data ?? []}
            selectedIds={editStudentIds}
            onChange={setEditStudentIds}
          />
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setEditingClass(null)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={saveStudents}
              disabled={savingStudents}
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {savingStudents ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Yoklama modalı */}
      <AttendanceModal
        open={!!attendanceClass}
        onClose={() => setAttendanceClass(null)}
        liveClassId={attendanceClass?.id ?? ""}
        className={attendanceClass?.title ?? ""}
        students={attendanceStudents}
        initial={attendanceData.data ?? {}}
      />

      {/* QR yoklama modalı */}
      <QrYoklamaModal
        open={!!qrClass}
        onClose={() => setQrClass(null)}
        roomCode={qrClass?.roomCode ?? ""}
        className={qrClass?.title ?? ""}
      />
    </div>
  );
}
